/**
 * ctgSearch public API: creates an InstantSearch-compatible search
 * client that newSearchComponent.vue and SearchInput.vue can mount in
 * place of algoliasearch(APP_ID, KEY).
 *
 * The returned client implements:
 *   - search(requests) -> Promise<{ results: [...] }>
 *   - searchForFacetValues(requests) -> Promise<{ ... }> (no-op stub)
 *
 * Requests arrive from vue-instantsearch's internal algoliasearch-helper
 * in the same shape it would send to Algolia; we translate each one to
 * a ctgSearch query() call.
 *
 * Index name: ctgSearch doesn't need one, but we honor the base name
 * "ctg_inventory" plus the "_price_asc" / "_year_desc" style suffixes
 * emitted by ais-sort-by so the dropdown's values survive the switch.
 */

import { buildIndex, query } from "./engine";
import { loadInventory } from "./store";


const BASE_INDEX = "ctg_inventory";
// Sort-by suffixes emitted by newSearchComponent.vue's ais-sort-by items
// (price_asc, price_desc, year_asc, year_desc, mileage_asc, mileage_desc).
// Engine.parseSortIndex() consumes the full indexName string.
export const INDEX_NAME = BASE_INDEX;


/* ------------------------------------------------------------------ */
/* Shared state                                                       */
/* ------------------------------------------------------------------ */

// One index instance is shared across all searchClient instances in
// this tab. The first createCtgSearchClient() kicks off the load; later
// calls wait on the same promise.
let indexPromise = null;
let indexInstance = null;

function ensureIndex() {
  if (indexInstance) return Promise.resolve(indexInstance);
  if (!indexPromise) {
    indexPromise = loadInventory({
      onUpdate: (fresh) => {
        // Background refresh landed. Swap in the new index; the next
        // query call picks it up. We don't force a UI rerender here --
        // the InstantSearch state hasn't changed, so the current view
        // is still valid. The user sees the fresh data on their next
        // refinement or page nav. If instant pickup becomes important,
        // add an event emitter here and hook newSearchComponent.vue to
        // call refresh() on the ais-instant-search root.
        indexInstance = buildIndex(fresh.cars);
      },
    }).then((blob) => {
      indexInstance = buildIndex(blob.cars);
      return indexInstance;
    });
  }
  return indexPromise;
}


/* ------------------------------------------------------------------ */
/* Algolia response shape helpers                                     */
/* ------------------------------------------------------------------ */

function algoliaResult(engineResult, indexName, startedAt) {
  return {
    hits: engineResult.hits,
    nbHits: engineResult.nbHits,
    page: engineResult.page,
    nbPages: engineResult.nbPages,
    hitsPerPage: engineResult.hitsPerPage,
    facets: engineResult.facets,
    facets_stats: {},
    query: engineResult.query,
    params: "", // the helper reads this back on some code paths; empty is safe
    index: indexName,
    processingTimeMS: Math.max(1, Date.now() - startedAt),
    exhaustiveFacetsCount: true,
    exhaustiveNbHits: true,
  };
}


function emptyResult(indexName) {
  return {
    hits: [],
    nbHits: 0,
    page: 0,
    nbPages: 0,
    hitsPerPage: 24,
    facets: {},
    facets_stats: {},
    query: "",
    params: "",
    index: indexName,
    processingTimeMS: 1,
    exhaustiveFacetsCount: true,
    exhaustiveNbHits: true,
  };
}


/* ------------------------------------------------------------------ */
/* Request translation                                                */
/* ------------------------------------------------------------------ */

function toEngineRequest(req) {
  const params = req.params || {};
  return {
    query:          params.query || "",
    page:           typeof params.page === "number" ? params.page : 0,
    hitsPerPage:    typeof params.hitsPerPage === "number" ? params.hitsPerPage : 24,
    facets:         Array.isArray(params.facets) ? params.facets : [],
    facetFilters:   Array.isArray(params.facetFilters) ? params.facetFilters : [],
    numericFilters: Array.isArray(params.numericFilters) ? params.numericFilters : [],
    sortIndex:      req.indexName || BASE_INDEX,
  };
}


/* ------------------------------------------------------------------ */
/* Factory                                                            */
/* ------------------------------------------------------------------ */

export function createCtgSearchClient(opts) {
  opts = opts || {};
  const exactOnly = !!opts.exactOnly;
  return {
    search(requests) {
      const started = Date.now();
      return ensureIndex().then((idx) => {
        const results = requests.map((req) => {
          try {
            const engineReq = toEngineRequest(req);
            engineReq.exactOnly = exactOnly;
            const r = query(idx, engineReq);
            return algoliaResult(r, req.indexName || BASE_INDEX, started);
          } catch (err) {
            // One bad request shouldn't nuke the whole batch; log and
            // emit an empty result in its slot. This matches how the
            // Algolia client degrades on a malformed request.
            // eslint-disable-next-line no-console
            console.error("ctgSearch request failed:", err, req);
            return emptyResult(req.indexName || BASE_INDEX);
          }
        });
        return { results: results };
      });
    },

    // Not used by the current Vue components but part of the client
    // contract -- vue-instantsearch's RefinementList in some modes calls
    // this for facet autocomplete. Returning an empty match is harmless
    // for our UI; the SRP uses pre-computed facet counts from search().
    searchForFacetValues(requests) {
      return Promise.resolve(requests.map(() => ({
        facetHits: [],
        exhaustiveFacetsCount: true,
        processingTimeMS: 1,
      })));
    },
  };
}
